// Iter 141 — Auto-refresh infrastructure untuk semua tab data-heavy.
//
// Prinsip:
//   1. Hanya berjalan saat tab browser visible (Page Visibility API).
//   2. Interval default 30 detik. Refresh diblokir bila sudah ada request in-flight.
//   3. Dirty guard: modul global `registerDirty()` menghentikan interval saat ada
//      form/modal aktif (mis. OrderForm, MasterModal, CashClosing, upload panel).
//   4. Manual refresh via tombol tidak diblokir oleh dirty guard tetapi tetap
//      diblokir bila in-flight (mencegah tumpang tindih).
//   5. Kegagalan (network/500) → data terakhir dipertahankan, error diekspos ke UI.

import { useCallback, useEffect, useRef, useState } from "react";
import { RefreshCw, AlertCircle } from "lucide-react";

// --- Dirty guard registry (module-level, dipakai semua panel/form) ---------
let __dirtyCount = 0;
const __dirtyListeners = new Set();
function __notifyDirty() { __dirtyListeners.forEach(fn => { try { fn(__dirtyCount); } catch {} }); }
export function registerDirty() {
  __dirtyCount += 1; __notifyDirty();
  let released = false;
  return () => { if (released) return; released = true; __dirtyCount = Math.max(0, __dirtyCount - 1); __notifyDirty(); };
}
export function isDirty() { return __dirtyCount > 0; }
export function subscribeDirty(fn) { __dirtyListeners.add(fn); return () => __dirtyListeners.delete(fn); }

// React hook pembungkus registerDirty untuk komponen form.
export function useDirtyGuard(enabled) {
  useEffect(() => {
    if (!enabled) return;
    const release = registerDirty();
    return release;
  }, [enabled]);
}

// --- Hook utama ------------------------------------------------------------
//   opts:
//     enabled: bool — matikan bila user belum login / role tidak berhak.
//     intervalMs: number (default 30000)
//     onRefresh: async fn — HARUS return promise (data). Fungsi wrapper akan
//       menangani in-flight + error. Jangan `throw` bila mau menahan data lama;
//       tetapi kalau throw, hook akan simpan error dan tidak menyentuh data
//       lama (yang dikelola oleh caller).
//     deps: array — reset timer saat berubah (mis. tab aktif berubah).
//     pauseWhenDirty: bool (default true) — bila true, interval berhenti saat
//       ada form dirty. Manual refresh tetap jalan.
export function useAutoRefresh({ enabled = true, intervalMs = 30000, onRefresh, deps = [], pauseWhenDirty = true } = {}) {
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const inFlightRef = useRef(false);
  const timerRef = useRef(null);
  const dirtyRef = useRef(__dirtyCount > 0);
  const visibleRef = useRef(typeof document === "undefined" || document.visibilityState === "visible");
  const onRefreshRef = useRef(onRefresh);
  useEffect(() => { onRefreshRef.current = onRefresh; }, [onRefresh]);

  const doRefresh = useCallback(async (opts = {}) => {
    if (!enabled) return;
    if (inFlightRef.current) return; // cegah stack
    inFlightRef.current = true;
    setIsRefreshing(true);
    try {
      const fn = onRefreshRef.current;
      if (typeof fn === "function") await fn(opts);
      setLastUpdated(new Date());
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Gagal memuat data.");
    } finally {
      inFlightRef.current = false;
      setIsRefreshing(false);
    }
  }, [enabled]);

  // Timer manager: hidup saat visible + enabled + (bukan dirty || !pauseWhenDirty).
  useEffect(() => {
    if (!enabled) return;
    const clear = () => { if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; } };
    const arm = () => {
      clear();
      if (!visibleRef.current) return;
      if (pauseWhenDirty && dirtyRef.current) return;
      timerRef.current = setInterval(() => { doRefresh({ auto: true }); }, intervalMs);
    };
    const onVis = () => { visibleRef.current = document.visibilityState === "visible"; arm(); };
    const onDirty = (n) => { dirtyRef.current = n > 0; arm(); };
    document.addEventListener("visibilitychange", onVis);
    const unsubDirty = subscribeDirty(onDirty);
    arm();
    return () => {
      clear();
      document.removeEventListener("visibilitychange", onVis);
      unsubDirty();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, intervalMs, pauseWhenDirty, doRefresh, ...deps]);

  return { lastUpdated, isRefreshing, error, refresh: () => doRefresh({ auto: false }) };
}

// --- UI helpers ------------------------------------------------------------
function formatUpdated(d) {
  if (!d) return "belum diperbarui";
  try {
    return d.toLocaleTimeString("id-ID", { timeZone: "Asia/Jakarta", hour: "2-digit", minute: "2-digit", second: "2-digit" });
  } catch { return d.toISOString(); }
}

export function RefreshIndicator({ testId = "refresh-indicator", lastUpdated, isRefreshing, error, onRefresh, compact }) {
  return (
    <span className={`refresh-indicator ${compact ? "compact" : ""}`} data-testid={testId}>
      <button
        type="button"
        className="refresh-btn"
        onClick={() => { if (!isRefreshing) onRefresh?.(); }}
        disabled={isRefreshing}
        data-testid={`${testId}-btn`}
        aria-label="Perbarui data sekarang"
        title="Perbarui data (Alt+R)"
      >
        <RefreshCw size={13} className={isRefreshing ? "spin" : ""} aria-hidden="true"/>
        <span>{isRefreshing ? "Memperbarui…" : "Muat Ulang"}</span>
      </button>
      <span className="refresh-meta" data-testid={`${testId}-meta`}>
        {error ? (
          <span className="refresh-err" title={error}>
            <AlertCircle size={12} aria-hidden="true"/> Gagal · data lama ditampilkan
          </span>
        ) : (
          <>Terakhir: <strong data-testid={`${testId}-time`}>{formatUpdated(lastUpdated)}</strong> WIB</>
        )}
      </span>
    </span>
  );
}

// Optional keyboard shortcut hook (Alt+R) — dipakai halaman utama.
export function useRefreshShortcut(onRefresh) {
  useEffect(() => {
    if (typeof window === "undefined") return;
    const handler = (e) => {
      if (e.altKey && (e.key === "r" || e.key === "R")) {
        // Jangan trigger jika user sedang mengetik di editable field.
        const t = e.target;
        const tag = String(t?.tagName || "").toUpperCase();
        if (tag === "INPUT" || tag === "TEXTAREA" || t?.isContentEditable) return;
        e.preventDefault();
        onRefresh?.();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onRefresh]);
}
